import { io } from 'socket.io-client';
import { baseURL } from './apiHandle';

// Remove trailing slash and /api suffix if present for socket connection
const SOCKET_URL = baseURL.replace(/\/+$/, '').replace(/\/api$/, '');

let socket = null;

/**
 * Initialize Socket.IO connection with authentication
 * @param {string} token - JWT authentication token
 * @returns {Socket} Socket.IO instance
 */
export const initializeSocket = (token) => {
  if (socket?.connected) {
    return socket;
  }

  socket = io(SOCKET_URL, {
    auth: {
      token: token,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    timeout: 20000,
  });

  // Connection events
  socket.on('connect', () => {
    // Socket connected
  });

  socket.on('disconnect', (reason) => {
    // Socket disconnected
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
  });

  socket.on('reconnect', (attemptNumber) => {
    // Reconnected
  });

  socket.on('reconnect_attempt', (attemptNumber) => {
    // Reconnection attempt
  });

  socket.on('reconnect_error', (error) => {
    console.error('Reconnection error:', error.message);
  });

  socket.on('reconnect_failed', () => {
    console.error('Reconnection failed');
  });

  return socket;
};

/**
 * Get the current socket instance
 * @returns {Socket|null} Socket.IO instance or null
 */
export const getSocket = () => socket;

/**
 * Disconnect socket
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Check if socket is connected
 * @returns {boolean} Connection status
 */
export const isConnected = () => {
  return socket?.connected || false;
};

/**
 * Join a conversation room
 * @param {string} conversationId - Conversation ID to join
 */
export const joinConversation = (conversationId) => {
  if (socket?.connected) {
    socket.emit('join_conversation', { conversationId });
  }
};

/**
 * Leave a conversation room
 * @param {string} conversationId - Conversation ID to leave
 */
export const leaveConversation = (conversationId) => {
  if (socket?.connected) {
    socket.emit('leave_conversation', { conversationId });
  }
};

/**
 * Send a message via socket
 * @param {Object} messageData - Message data
 */
export const sendSocketMessage = (messageData) => {
  if (socket?.connected) {
    socket.emit('send_message', messageData);
  }
};

/**
 * Start typing indicator
 * @param {string} conversationId - Conversation ID
 */
export const emitTypingStart = (conversationId) => {
  if (socket?.connected) {
    socket.emit('typing_start', { conversationId });
  }
};

/**
 * Stop typing indicator
 * @param {string} conversationId - Conversation ID
 */
export const emitTypingStop = (conversationId) => {
  if (socket?.connected) {
    socket.emit('typing_stop', { conversationId });
  }
};

/**
 * Mark messages as read
 * @param {string} conversationId - Conversation ID
 * @param {Array} messageIds - Array of message IDs (optional)
 */
export const emitMarkRead = (conversationId, messageIds = []) => {
  if (socket?.connected) {
    socket.emit('mark_read', { conversationId, messageIds });
  }
};

/**
 * Get online status of users
 * @param {Array} userIds - Array of user IDs
 */
export const getOnlineStatus = (userIds) => {
  if (socket?.connected) {
    socket.emit('get_online_status', { userIds });
  }
};

/**
 * Subscribe to fleet tracking room (fleet managers/owners only)
 */
export const subscribeFleetTracking = () => {
  if (socket?.connected) {
    socket.emit('subscribe_fleet_tracking');
  }
};

/**
 * Unsubscribe from fleet tracking room
 */
export const unsubscribeFleetTracking = () => {
  if (socket?.connected) {
    socket.emit('unsubscribe_fleet_tracking');
  }
};

/**
 * Request a fresh fleet snapshot
 */
export const requestFleetSnapshot = () => {
  if (socket?.connected) {
    socket.emit('request_fleet_snapshot');
  }
};

/**
 * Listen for a socket event and return an unsubscribe function
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @returns {Function} Unsubscribe function
 */
export const onSocketEvent = (event, handler) => {
  if (socket) {
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }
  return () => {};
};

export default {
  initializeSocket,
  getSocket,
  disconnectSocket,
  isConnected,
  joinConversation,
  leaveConversation,
  sendSocketMessage,
  emitTypingStart,
  emitTypingStop,
  emitMarkRead,
  getOnlineStatus,
  subscribeFleetTracking,
  unsubscribeFleetTracking,
  requestFleetSnapshot,
  onSocketEvent,
};
